import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Info, 
  Utensils, 
  Activity,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

function FoodDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [food, setFood] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedWeight, setSelectedWeight] = useState(25);
  const [selectedActivity, setSelectedActivity] = useState('moderate');
  const [feedingGuideline, setFeedingGuideline] = useState(null);

  useEffect(() => {
    fetchFoodDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (food) {
      calculateFeeding();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeight, selectedActivity, food]);

  const fetchFoodDetails = async () => {
    try {
      const response = await api.get(`/nutrition/foods/${id}`);
      setFood(response.data);
    } catch (error) {
      console.error('Error fetching food details:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateFeeding = async () => {
    try {
      const response = await api.post('/nutrition/feeding-calculator', {
        food_id: id,
        weight: selectedWeight,
        age_months: 24, // Default to adult
        activity_level: selectedActivity
      });
      setFeedingGuideline(response.data);
    } catch (error) {
      console.error('Error calculating feeding:', error);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!food) return <div>Food not found</div>;

  const nutritionalInfo = food.nutritional_info || {};
  const primaryIngredients = food.ingredients?.filter(i => i.is_primary) || [];
  const secondaryIngredients = food.ingredients?.filter(i => !i.is_primary) || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/app/nutrition')}
          className="flex items-center space-x-2 text-purple-600 hover:text-purple-800 mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Nutrition</span>
        </button>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-8">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-bold mb-2">{food.name}</h1>
                <p className="text-xl opacity-90">{food.brand}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                food.type === 'dry' ? 'bg-orange-500' :
                food.type === 'wet' ? 'bg-blue-500' :
                'bg-green-500'
              }`}>
                {food.type}
              </span>
            </div>
            {food.special_diet && (
              <div className="mt-4">
                <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm">
                  {food.diet_type}
                </span>
              </div>
            )}
          </div>

          <div className="p-8">
            {food.description && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-3 flex items-center">
                  <Info className="w-5 h-5 mr-2 text-purple-600" />
                  Description
                </h2>
                <p className="text-gray-700">{food.description}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <Utensils className="w-5 h-5 mr-2 text-purple-600" />
                  Ingredients
                </h2>
                
                {primaryIngredients.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-600 mb-2">Primary Ingredients</h3>
                    {primaryIngredients.map((ingredient) => (
                      <div key={ingredient.id} className="mb-3 p-3 bg-purple-50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <span className="font-medium text-gray-900">{ingredient.name}</span>
                          {ingredient.percentage && (
                            <span className="text-sm text-gray-600">{ingredient.percentage}%</span>
                          )}
                        </div>
                        {ingredient.benefits && (
                          <p className="text-sm text-gray-600 mt-1">{ingredient.benefits}</p>
                        )}
                        {ingredient.is_common_allergen && (
                          <div className="flex items-center mt-2 text-yellow-600">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            <span className="text-xs">Common allergen</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {secondaryIngredients.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-600 mb-2">Other Ingredients</h3>
                    <div className="space-y-1">
                      {secondaryIngredients.map((ingredient) => (
                        <div key={ingredient.id} className="flex justify-between text-sm">
                          <span className="text-gray-700">{ingredient.name}</span>
                          {ingredient.percentage && (
                            <span className="text-gray-500">{ingredient.percentage}%</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <Activity className="w-5 h-5 mr-2 text-purple-600" />
                  Nutritional Information
                </h2>
                <div className="space-y-3">
                  <div className="flex justify-between p-3 bg-gray-50 rounded">
                    <span className="font-medium">Calories per Cup</span>
                    <span>{nutritionalInfo.calories_per_cup || 'N/A'}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-gray-50 rounded">
                      <div className="text-sm text-gray-600">Protein</div>
                      <div className="font-semibold">{nutritionalInfo.protein_percentage || 'N/A'}%</div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded">
                      <div className="text-sm text-gray-600">Fat</div>
                      <div className="font-semibold">{nutritionalInfo.fat_percentage || 'N/A'}%</div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded">
                      <div className="text-sm text-gray-600">Fiber</div>
                      <div className="font-semibold">{nutritionalInfo.fiber_percentage || 'N/A'}%</div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded">
                      <div className="text-sm text-gray-600">Moisture</div>
                      <div className="font-semibold">{nutritionalInfo.moisture_percentage || 'N/A'}%</div>
                    </div>
                  </div>
                  {nutritionalInfo.calcium_percentage && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-gray-50 rounded">
                        <div className="text-sm text-gray-600">Calcium</div>
                        <div className="font-semibold">{nutritionalInfo.calcium_percentage}%</div>
                      </div>
                      <div className="p-3 bg-gray-50 rounded">
                        <div className="text-sm text-gray-600">Phosphorus</div>
                        <div className="font-semibold">{nutritionalInfo.phosphorus_percentage || 'N/A'}%</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t pt-8">
              <h2 className="text-xl font-semibold mb-4">Feeding Calculator</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dog Weight (lbs)
                  </label>
                  <input
                    type="number"
                    value={selectedWeight}
                    onChange={(e) => setSelectedWeight(parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    min="1"
                    max="200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Activity Level
                  </label>
                  <select
                    value={selectedActivity}
                    onChange={(e) => setSelectedActivity(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="low">Low Activity</option>
                    <option value="moderate">Moderate Activity</option>
                    <option value="high">High Activity</option>
                    <option value="very_high">Very High Activity</option>
                  </select>
                </div>
              </div>

              {feedingGuideline && (
                <div className="bg-purple-50 rounded-lg p-6">
                  <h3 className="font-semibold text-lg mb-3">Recommended Daily Amount</h3>
                  <div className="text-2xl font-bold text-purple-600 mb-2">
                    {feedingGuideline.daily_amount_min} - {feedingGuideline.daily_amount_max} cups
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    Split into {feedingGuideline.feeding_frequency} meals per day
                  </p>
                  {feedingGuideline.notes && (
                    <div className="flex items-start space-x-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                      <p>{feedingGuideline.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FoodDetail;